/**
 * ============================================================
 * PGLP | MONTE HERMOM
 * COMPONENTE: ImovelParadigma_inicial
 * VERSÃO: 3.5.0
 * ------------------------------------------------------------
 * NOVIDADES v3.5.0:
 *   ✔ Recebe laudoIdNumerico (ID numérico do laudo)
 *   ✔ Engine de Prazo usa o ID numérico correto
 *   ✔ Fallback silencioso quando não há laudoIdNumerico
 *
 * v3.4.0:
 *   ✔ Integra Engine de Prazo (usePrazoEngine + PrazoBar)
 * ============================================================
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Scale, MapPin, Fingerprint, ClipboardList, FolderLock, Phone,
  ShieldCheck, Calculator, Building2,
} from 'lucide-react';

import { salvarEstadoSoberano } from '../../services/ImovelPersistenceService';
import RequerenteIdentificacao from '../requerente/Requerente_Identificacao';
import ImovelParadigma_Ingestao from './ImovelParadigma_Ingestao';
import ImovelParadigma_identificacao from './ImovelParadigma_identificacao';
import ImovelParadigma_Documentos from './ImovelParadigma_Documentos';
import ImovelParadigma_caracterizacao from './ImovelParadigma_caracterizacao';
import ImovelParadigma_planejamento from './ImovelParadigma_planejamento';
import ImovelParadigma_QuickUpload from './ImovelParadigma_QuickUpload';
import usePrazoEngine from '../../hooks/usePrazoEngine';
import PrazoBar from './PrazoBar';

const gerarUuid = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `mh-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const SUB_ETAPAS = [
  { codigo: 'REQUERENTE',     label: 'Requerente',    icon: Scale },
  { codigo: 'INGESTAO',       label: 'Ingestão',      icon: MapPin },
  { codigo: 'IDENTIFICACAO',  label: 'Identificação', icon: Fingerprint },
  { codigo: 'DOCUMENTOS',     label: 'Documentos',    icon: FolderLock },
  { codigo: 'CARACTERIZACAO', label: 'Caracterização',icon: ClipboardList },
  { codigo: 'PLANEJAMENTO',   label: 'Planejamento',  icon: Phone },
];

const ESTADO_INICIAL_REQUERENTE = {
  nome: '', documento: '',
  tribunal_id: null, tribunal_nome: '', uf: '',
  comarca_id: null, comarca_nome: '',
  forum_id: null, forum: '',
  unidade_id: null, unidade_nome: '',
  vara: '', processoJudicial: '',
  finalidade_codigo: '', finalidade_nome: '',
  objetivo: '', texto_laudo: '',
  seloIntegridade: `REQ-${new Date().getFullYear()}-MH`,
  statusJudicial: 'ATIVO',
};

function chaveStorage(laudoId, codigo) {
  const chave = laudoId || codigo || 'sem-laudo';
  return `MH_abertura_imovel_${chave}`;
}

function carregarEstadoLocal(laudoId, codigo) {
  try {
    const raw = localStorage.getItem(chaveStorage(laudoId, codigo));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function salvarEstadoLocal(laudoId, codigo, payload) {
  try {
    localStorage.setItem(
      chaveStorage(laudoId, codigo),
      JSON.stringify({ ...payload, _salvoEm: new Date().toISOString() })
    );
  } catch (e) {
    console.error('[Abertura] Falha ao salvar localmente:', e);
  }
}

export default function ImovelParadigma({
  idRegistro,
  laudoId,
  laudoIdNumerico,        // ← NOVO
  numeroLaudo,
  codigo,
  especialistaResponsavel,
  dadosIniciais,
  etapaInicial,
  onCompleteAbertura,
}) {
  const refPersistencia = laudoId || numeroLaudo || codigo || idRegistro || 'sem-laudo';
  const hidratadoRef = useRef(false);

  const estadoSalvo = carregarEstadoLocal(laudoId || numeroLaudo, codigo);

  const etapaDetectada = etapaInicial || estadoSalvo?.etapa || 'REQUERENTE';
  const [etapa, setEtapa] = useState(etapaDetectada);

  const [perfilAtivo] = useState(
    (typeof localStorage !== 'undefined' && localStorage.getItem('MH_STATUS_TECNICO')) || 'ASSISTENTE'
  );

  const [requerente, setRequerente] = useState({
    ...ESTADO_INICIAL_REQUERENTE,
    ...(dadosIniciais?.requerente || estadoSalvo?.requerente || {}),
  });

  const [dadosImovel, setDadosImovel] = useState({
    id: dadosIniciais?.id || idRegistro || gerarUuid(),
    laudo_id: dadosIniciais?.laudo_id || laudoId || null,
    laudoRef: dadosIniciais?.laudoRef || numeroLaudo || '',

    latitude: '', longitude: '',
    areaTerreno: '', frente: '',
    logradouro: '', numero: '', complemento: '',
    bairro: '', cidade: '', uf: '', cep: '',
    matricula: '', contribuinte: '', cartorio: '',
    uso: '', tipologia: '', padrao: '',
    idadeImovel: '', idadeReal: '', vidaUtil: '',
    vidaUtilIBAPE: null,
    estadoConservacao: 'c',
    statusConformidade: true,
    logAgendamento: [],
    geocodificacao: null,
    documentos: {},
    dadosMatricula: null,
    topografia: null,
    fatorTopografia: null,
    estadoMatricula: null,

    ...(dadosIniciais || {}),
    ...(estadoSalvo?.dadosImovel || {}),
  });

  const idxAtual = SUB_ETAPAS.findIndex((e) => e.codigo === etapa);

  /* ============================================================
     ENGINE DE PRAZO — usa o ID NUMÉRICO do laudo
     ============================================================ */

  const prazoEngine = usePrazoEngine({
    laudoId: laudoIdNumerico || null,
    ciclo: 1,
    faseCodigo: etapa,
    autoSessao: true,
    autoPolling: true,
    intervaloPolling: 60 * 1000,
  });

  useEffect(() => {
    hidratadoRef.current = true;
  }, []);

  useEffect(() => {
    if (!hidratadoRef.current) return;

    const nadaPreenchido =
      etapa === 'REQUERENTE' &&
      !requerente.processoJudicial &&
      !requerente.nome &&
      !dadosImovel.logradouro;

    if (nadaPreenchido) return;

    const payload = {
      etapa,
      requerente,
      dadosImovel: { ...dadosImovel, _etapa_atual: etapa },
      _versao: '3.5.0',
    };
    salvarEstadoLocal(laudoId || numeroLaudo, codigo, payload);
  }, [etapa, requerente, dadosImovel, laudoId, numeroLaudo, codigo]);

  useEffect(() => {
    if (!refPersistencia || refPersistencia === 'sem-laudo') return undefined;
    if (!hidratadoRef.current) return undefined;

    const timer = window.setTimeout(() => {
      salvarEstadoSoberano(
        requerente,
        { ...dadosImovel, _etapa_atual: etapa },
        { laudoId: refPersistencia }
      ).catch((e) => {
        console.warn('[Abertura] Falha no save backend:', e?.message);
      });
    }, 1500);

    return () => window.clearTimeout(timer);
  }, [requerente, dadosImovel, refPersistencia, etapa]);

  const avancar = useCallback(() => {
    if (idxAtual < SUB_ETAPAS.length - 1) {
      setEtapa(SUB_ETAPAS[idxAtual + 1].codigo);
    } else if (typeof onCompleteAbertura === 'function') {
      onCompleteAbertura({
        requerente,
        dadosImovel,
        etapaAtual: etapa,
      });
    }
  }, [idxAtual, onCompleteAbertura, requerente, dadosImovel, etapa]);

  const voltar = useCallback(() => {
    if (idxAtual > 0) setEtapa(SUB_ETAPAS[idxAtual - 1].codigo);
  }, [idxAtual]);

  const handleQuickUpload = useCallback((tipo, file) => {
    setDadosImovel((prev) => ({
      ...prev,
      documentos: {
        ...(prev.documentos || {}),
        [tipo]: {
          status: 'pendente',
          file,
          meta: {
            nome: file.name,
            size: file.size,
            tipo: file.type,
            uploadedEm: new Date().toISOString(),
            origem: 'QUICK_UPLOAD',
          },
        },
      },
    }));
  }, []);

  const especialista = especialistaResponsavel || 'Não informado';

  return (
    <div className="w-full min-h-screen bg-slate-950 px-4 py-4 lg:px-6 lg:py-5 space-y-4 text-white">

      {/* HEADER */}
      <header className="bg-slate-900 rounded-2xl px-5 py-3.5 border border-white/5 shadow-lg">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 bg-amber-400 rounded-xl flex items-center justify-center shrink-0">
              <Building2 size={16} className="text-slate-950" />
            </div>
            <div className="min-w-0">
              <p className="text-[8px] font-black text-amber-400 uppercase tracking-[0.25em] leading-none">
                Abertura · Imóvel
              </p>
              <h1 className="text-base font-black uppercase italic tracking-tight text-white leading-tight mt-1 truncate">
                {requerente.processoJudicial || 'Novo Processo'}
              </h1>
            </div>
          </div>

          <div className="hidden lg:flex items-center gap-2 shrink-0">
            <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">
              {especialista}
            </span>
            <span className="text-slate-700">•</span>
            <span className="text-[9px] font-black text-white uppercase tracking-widest">
              {numeroLaudo || '—'}
            </span>
            <span className="text-slate-700">•</span>
            <span className="text-[9px] font-black text-amber-400 uppercase tracking-widest">
              {requerente.seloIntegridade}
            </span>
          </div>
        </div>

        {/* ENGINE DE PRAZO */}
        {prazoEngine.estado && (
          <div className="mt-3">
            <PrazoBar
              estado={prazoEngine.estado}
              compacto={false}
              onRegistrarProvidencia={prazoEngine.registrarProvidencia}
              onPrepararProrrogacao={prazoEngine.prorrogarPrazo}
              mostrarAcoesCriticas={true}
            />
          </div>
        )}

        {/* Barra de progresso */}
        <div className="flex gap-1 mt-3">
          {SUB_ETAPAS.map((e, i) => {
            const Icon = e.icon;
            const ativo = i === idxAtual;
            const concluido = i < idxAtual;
            return (
              <div key={e.codigo} className="flex-1">
                <div
                  className={`w-full h-0.5 rounded-full transition-all duration-500 ${
                    ativo ? 'bg-amber-400' : concluido ? 'bg-emerald-500' : 'bg-slate-800'
                  }`}
                />
                <div
                  className={`flex items-center gap-1 mt-1.5 transition-all ${
                    ativo ? 'text-amber-400' : concluido ? 'text-emerald-500' : 'text-slate-700'
                  }`}
                >
                  <Icon size={9} />
                  <span className="text-[6.5px] font-black uppercase tracking-wider">
                    {e.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </header>

      {/* CONTEÚDO */}
      <main className="relative z-10">
        {etapa === 'REQUERENTE' && (
          <RequerenteIdentificacao
            requerente={requerente}
            setRequerente={setRequerente}
            onNext={avancar}
          />
        )}

        {etapa === 'INGESTAO' && (
          <ImovelParadigma_Ingestao
            reportNumber={numeroLaudo}
            dadosImovel={dadosImovel}
            onChange={(dados) => setDadosImovel((prev) => ({ ...prev, ...dados }))}
            onComplete={(dados) => {
              setDadosImovel((prev) => ({ ...prev, ...dados }));
              avancar();
            }}
          />
        )}

        {etapa === 'IDENTIFICACAO' && (
          <ImovelParadigma_identificacao
            formData={dadosImovel}
            setFormData={setDadosImovel}
            requerente={requerente}
            onBack={voltar}
            onNext={avancar}
          />
        )}

        {etapa === 'DOCUMENTOS' && (
          <ImovelParadigma_Documentos
            reportNumber={numeroLaudo}
            documentosExistentes={dadosImovel.documentos || {}}
            onBack={voltar}
            onComplete={(payload) => {
              setDadosImovel((prev) => ({
                ...prev,
                documentos: payload?.documentos || {},
              }));
              avancar();
            }}
          />
        )}

        {etapa === 'CARACTERIZACAO' && (
          <ImovelParadigma_caracterizacao
            formData={dadosImovel}
            setFormData={setDadosImovel}
            onBack={voltar}
            onNext={avancar}
          />
        )}

        {etapa === 'PLANEJAMENTO' && (
          <ImovelParadigma_planejamento
            formData={dadosImovel}
            setFormData={setDadosImovel}
            onBack={voltar}
            onNext={avancar}
          />
        )}
      </main>

      {/* CITAÇÃO NORMATIVA */}
      <section className="bg-slate-900 rounded-2xl px-5 py-3 border border-white/5 flex items-center gap-4">
        <div className="bg-slate-950 p-2.5 rounded-xl border border-white/5 shrink-0">
          <Calculator size={18} className="text-amber-400" />
        </div>
        <p className="text-[9px] font-bold text-slate-400 leading-relaxed uppercase">
          A localização é obtida por geocodificação do endereço informado. A confirmação
          humana permanece obrigatória antes da continuidade do fluxo.
        </p>
      </section>

      {/* RODAPÉ */}
      <footer className="flex justify-between items-center bg-slate-900 rounded-2xl px-5 py-3 border border-white/5">
        <div className="flex items-center gap-2.5">
          <ShieldCheck
            className={perfilAtivo === 'PERITO' ? 'text-emerald-500' : 'text-amber-500'}
            size={16}
          />
          <div>
            <span className="block text-[7px] font-black text-slate-500 uppercase tracking-[0.2em] leading-none">
              Modo de Operação
            </span>
            <span className="text-[10px] font-black uppercase italic tracking-widest text-white">
              {perfilAtivo} SOBERANO
            </span>
          </div>
        </div>
      </footer>

      {/* UPLOAD RÁPIDO */}
      <ImovelParadigma_QuickUpload
        documentosAtuais={dadosImovel.documentos || {}}
        onUpload={handleQuickUpload}
      />
    </div>
  );
}